import React from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import billingUtils from 'app/shared/utils/billingUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

import { NOTIFICATION_TYPE } from 'app/client/workers/notificationModule';
import { Spinner } from '@zillow/zrs-comet';

import {
    Text,
    LabeledControl,
    FormField,
    Radio,
    Label,
    Checkbox,
    Button,
    TextButton,
    IconPlus,
    Spacer,
    Alert,
} from '@zillow/constellation';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import BillingError from 'app/shared/components/Billing/BillingError';
import TermsAndConditionsWidget from './TermsAndConditionsWidget';

import { StyledButtonGroup, StyledAlertText } from './StyledComponents';

const { PAYMENT_TYPES } = BillingConstants;

const propTypes = {
    accountNumber: PropTypes.string.isRequired,
    submitLabel: PropTypes.string,
    accountPaymentMethods: PropTypes.arrayOf(PropTypes.shape({
        paymentMethodId: PropTypes.string.isRequired,
        brand: PropTypes.string,
        maskNumber: PropTypes.string.isRequired,
        expirationMonth: PropTypes.number,
        expirationYear: PropTypes.number,
    })).isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitDisabled: PropTypes.bool,
};

const defaultProps = {
    submitLabel: 'Save',
    submitDisabled: false,
};

const BillingPaymentMethods = ({
    dispatch,
    accountNumber,
    submitLabel,
    accountPaymentMethodsLoadingState,
    accountPaymentMethods,
    onSubmit,
    submitDisabled,
    paymentMethodLock,
    isPaymentInProgress,
    isSinglePayment,
    infoMessage,
    isAutopayEnabled,
    autopayMethodId,
    autoPayDisabled,
    allowRemove,
    userCanAccessAch,
}) => {
    const isEditPaymentMethodsPage = !isSinglePayment;
    const showAutopay = isEditPaymentMethodsPage;

    const cookieEnabledServices = useSelector(state => state.app.cookieEnabledServices);
    const canAccessAch = Boolean(cookieEnabledServices.ZG_ACH_PAYMENTS_ENABLED) || userCanAccessAch;
    const impersonatorEmail = useSelector(state => state.user.impersonatorEmail);
    const impersonatorUserToken = useSelector(state => state.user.impersonatorUserToken);
    const isProduction = useSelector(state => state.env.isProduction);
    const disableImpersonatorPayments = isProduction && (impersonatorEmail || impersonatorUserToken);

    const [autoPay, setAutoPay] = React.useState(isAutopayEnabled);
    const [autoPayConditionsAccepted, setAutoPayConditionsAccepted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedCreditCardId, setSelectedCreditCardId] = React.useState(autopayMethodId);
    const [errorMessage, setErrorMessage] = React.useState(null);

    const selectedPaymentMethod = accountPaymentMethods
        .find(method => method.paymentMethodId === selectedCreditCardId);
    const isAchCurrentlySelected = selectedPaymentMethod && selectedPaymentMethod.paymentType === 'ACH';

    React.useEffect(() => {
        const userContainsPaymentMethod = accountPaymentMethods.some(paymentMethod => paymentMethod.paymentMethodId
            === autopayMethodId);
        setSelectedCreditCardId(userContainsPaymentMethod ? autopayMethodId : null);
        setAutoPay(isAutopayEnabled);
    }, [accountPaymentMethods]);

    React.useEffect(() => {
        if (autoPayDisabled) {
            setAutoPay(false);
            setAutoPayConditionsAccepted(false);
        }
    }, [autoPayDisabled]);

    const handleGetPaymentMethods = () => {
        dispatch(BillingActions.getPaymentMethodsForAccount(accountNumber))
            .catch(() => {
                // todo display error
            });
    };

    const handleWebsocketMessge = (message) => {
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== NOTIFICATION_TYPE.BILLING_PORTAL_PAYMENT_METHODS_REFRESH) {
            return;
        }
        const dtoData = JSON.parse(data.payload);
        if (dtoData.accountNumber === accountNumber) {
            dispatch(BillingActions.setPaymentMethodLock(false));
            handleGetPaymentMethods(true);
        }
    };

    const handleCreditCardAdded = () => {
        dispatch(ModalActions.hideModal());
        dispatch(BillingActions.setPaymentMethodLock(true));
        dispatch(BillingActions.importPaymentMethods(accountNumber));
    };

    React.useEffect(() => {
        handleGetPaymentMethods(true, true);
        window.rentalManagerApiWebSocket.addEventListener('message', handleWebsocketMessge);
        return () => window.rentalManagerApiWebSocket.removeEventListener('message', handleWebsocketMessge);
    }, []);

    const canSubmit = () => {
        if (submitDisabled) return false;

        // If in edit card mode
        if (isEditPaymentMethodsPage) {
            // no changes
            if ((isAutopayEnabled === autoPay && autopayMethodId)
                === selectedCreditCardId && selectedCreditCardId !== null) {
                return false;
            }
            // Autopay is on but user changed the card
            if (isAutopayEnabled === autoPay && autopayMethodId
                !== selectedCreditCardId && selectedCreditCardId !== null) {
                return autoPayConditionsAccepted;
            }
            if (isAutopayEnabled && !autoPay) {
                return true; // User turned off autopay
            }
            if (!isAutopayEnabled && autoPay && autopayMethodId === selectedCreditCardId) {
                // User toggles on autopay, and default is already selected
                return autoPayConditionsAccepted;
            }
            if (!isAutopayEnabled && autoPay) {
                return (autopayMethodId !== selectedCreditCardId)
                    && selectedCreditCardId !== null && autoPayConditionsAccepted;
            }
        }

        // Single Payment Mode
        if (!selectedCreditCardId) {
            // No card selected at all
            return false;
        }
        if (isAchCurrentlySelected) {
            // Confirmation necessary for ACH
            return autoPayConditionsAccepted;
        }
        return true;
    };

    const submit = () => {
        setIsSubmitting(true);
        dispatch(ModalActions.hideModal());
        dispatch(BillingActions.setPaymentMethodLock(true));
        onSubmit(selectedCreditCardId, autoPayConditionsAccepted)
            .then(() => {
                setErrorMessage(null);
                setIsSubmitting(false);
            }).catch((err) => {
                setErrorMessage(err.message);
                setIsSubmitting(false);
            });
    };

    const handleSubmit = () => {
        // only display modal if its the edit payment methods page AND user disabled autopay
        if (!isSinglePayment && isAutopayEnabled === true && !autoPay) {
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.CONFIRMATION_MODAL,
                header: 'Are you sure you want to cancel auto pay?',
                submitButtonTitle: 'Keep Auto Pay',
                cancelButtonTitle: 'Cancel Auto Pay',
                textContent: 'Auto pay lets you avoid the risk of missed payments. '
                    + 'If you cancel, you’ll have to pay manually each month.',
                onCancel: submit,
                onSubmit: () => dispatch(ModalActions.hideModal()),
            }));
            return;
        }
        submit();
    };

    const handleRemoveCreditCardClick = (creditCard) => {
        const isAchSelected = creditCard.paymentType === 'ACH';
        const endingNumbers = creditCard.maskNumber.slice(creditCard.maskNumber.length - 4,
            creditCard.maskNumber.length);
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            header: `Are you sure you want to remove 
                ${isAchSelected ? 'bank account' : 'card'} ${endingNumbers}?`,
            submitButtonTitle: 'Yes, remove it',
            textContent: `Removing this payment method 
                will not cancel any of your ongoing payments that use this 
                ${isAchSelected ? 'bank account' : 'card'}.`,
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                setIsSubmitting(true);
                dispatch(analyticsUtils.track
                    .GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_REMOVE_PAYMENT_METHOD));
                dispatch(BillingActions.setPaymentMethodLock(true));
                dispatch(ModalActions.hideModal());
                dispatch(BillingActions.deletePaymentMethod({
                    paymentMethodId: creditCard.paymentMethodId,
                    accountNumber,
                })).then((res) => {
                    if (!res.success) throw res.error.message;
                }).then(() => {
                    setErrorMessage(null);
                    setIsSubmitting(false);
                }).catch((err) => {
                    setErrorMessage(err.message);
                    setIsSubmitting(false);
                    dispatch(BillingActions.setPaymentMethodLock(false));
                });
            },
        }));
    };

    const handleAddCreditCardClick = (paymentMethodType) => {
        dispatch(BillingActions.setPaymentMethodType(paymentMethodType));
        dispatch(ModalActions.showModal({
            header: 'Add a new payment method',
            onCreditCardAdded: handleCreditCardAdded,
            accountNumber,
            modalType: ModalConstants.BILLING_ADD_PAYMENT_MODAL,
        }));
    };

    const showConfirmation = () => {
        if (isSinglePayment) {
            // On single payment page only show confirmation checkbox if is ACH
            return isAchCurrentlySelected;
        }
        if (!showAutopay) {
            return false;
        }
        // edit payment methods
        if (autoPay) {
            return !isAutopayEnabled || (autopayMethodId !== selectedCreditCardId);
        }
        return false;
    };

    const isLoading = accountPaymentMethodsLoadingState === LoadingConstants.LOADING
        || accountPaymentMethodsLoadingState === LoadingConstants.INITIAL;
    const errorLoadingPaymentMethods = accountPaymentMethodsLoadingState === LoadingConstants.ERROR;

    const disableControls = () => isLoading || errorLoadingPaymentMethods || paymentMethodLock
        || isPaymentInProgress || disableImpersonatorPayments;

    if (errorLoadingPaymentMethods) {
        return (
            <Alert
                appearance="error"
                body="Error loading payment methods, please try again later."
            />
        );
    }

    const filteredPaymentMethods = canAccessAch ? accountPaymentMethods : accountPaymentMethods
        .filter(paymentMethod => paymentMethod.paymentType === 'CreditCard');

    const formatPaymentMethod = (paymentMethod) => {
        if (paymentMethod.paymentType === 'CreditCard') {
            return `${paymentMethod.brand} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
        }
        if (paymentMethod.paymentType === 'ACH') {
            return `${paymentMethod.bankName} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
        }
        return 'Unknown Payment Method';
    };

    return (
        <React.Fragment>
            <FormField>
                <Label required={true}>
                    <Text fontType="h6">
                        Payment method:
                    </Text>
                </Label>
                {isLoading && <SpinnerContainer />}
                {paymentMethodLock && !isPaymentInProgress && (
                    <StyledAlertText>
                        <Text paddingRight="sm">
                            Please wait while we update your payment methods.
                        </Text>
                        <Spinner />
                    </StyledAlertText>
                )}
                {filteredPaymentMethods.map(paymentMethod => (
                    <LabeledControl
                        key={paymentMethod.paymentMethodId}
                        disabled={disableControls()}
                        controlId={`LabeledControl-credit-card-${paymentMethod.paymentMethodId}`}
                        label={(
                            <Label>
                                {formatPaymentMethod(paymentMethod)}
                                {allowRemove && !paymentMethod.defaultPaymentMethod
                                    && !paymentMethodLock && !isPaymentInProgress && !disableImpersonatorPayments
                                    && (
                                        <TextButton
                                            onClick={() => handleRemoveCreditCardClick(paymentMethod)}
                                            disabled={disableControls()}
                                        >
                                            Remove
                                        </TextButton>
                                    )}
                                {autopayMethodId === paymentMethod.paymentMethodId && isAutopayEnabled && (
                                    <Text fontType="bodySmall">
                                        (Default)
                                    </Text>
                                )}
                                {paymentMethod.paymentType === 'CreditCard' && (
                                    <>
                                        <br />
                                        <Text fontType="bodySmall" fontColor="textLight">
                                            {`Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`}
                                        </Text>
                                    </>
                                )}
                            </Label>
                        )}
                        control={(
                            <Radio
                                name={`LabeledControl-credit-card-${paymentMethod.paymentMethodId}`}
                                checked={selectedCreditCardId === paymentMethod.paymentMethodId}
                                onChange={() => {
                                    setSelectedCreditCardId(paymentMethod.paymentMethodId);
                                    setAutoPayConditionsAccepted(false);
                                }}
                                disabled={disableControls()}
                            />
                        )}
                    />
                ))}
            </FormField>
            <Spacer marginY="sm">
                <Button
                    buttonType="secondary"
                    fluid={true}
                    icon={<IconPlus />}
                    onClick={() => handleAddCreditCardClick(PAYMENT_TYPES.CREDIT_CARD)}
                    disabled={disableControls()}
                >
                    Add Payment Method
                </Button>
            </Spacer>
            {showAutopay && (
                <FormField>
                    <LabeledControl
                        controlId="LabeledControl-checkbox-autopay"
                        label={<Label>Auto Pay</Label>}
                        control={(
                            <Checkbox
                                name="LabeledControl-checkbox-autopay"
                                checked={autoPay}
                                onChange={() => {
                                    setAutoPay(!autoPay);
                                    setAutoPayConditionsAccepted(false);
                                }}
                            />
                        )}
                        disabled={autoPayDisabled || disableControls()}
                    />
                </FormField>
            )}
            {showConfirmation() && (
                <React.Fragment>
                    <Spacer margin="sm">
                        <Spacer marginY="sm">
                            <Text fontType="bodySmall" as="b">
                                Terms & Conditions
                            </Text>
                        </Spacer>
                        <TermsAndConditionsWidget
                            accountPaymentMethods={accountPaymentMethods}
                            selectedCreditCardId={selectedCreditCardId}
                            isSinglePayment={isSinglePayment}
                        />
                    </Spacer>
                    <FormField>
                        <LabeledControl
                            controlId="LabeledControl-checkbox-autopay-conditions"
                            label={<Label>Yes, I accept the Terms & Conditions</Label>}
                            disabled={disableControls()}
                            control={(
                                <Checkbox
                                    name="LabeledControl-checkbox-autopay-conditions"
                                    checked={autoPayConditionsAccepted}
                                    onChange={() => setAutoPayConditionsAccepted(!autoPayConditionsAccepted)}
                                />
                            )}
                        />
                    </FormField>
                </React.Fragment>
            )}
            {infoMessage && (
                <Spacer margin="sm">
                    <Text fontType="bodySmall" as="p">
                        *
                        {infoMessage}
                    </Text>
                </Spacer>
            )}
            {showAutopay && autoPayDisabled && (
                <Spacer margin="sm">
                    <Text fontType="bodySmall" as="p">*Auto pay is only available if you pay the full balance.</Text>
                </Spacer>
            )}
            {errorMessage && (
                <BillingError errorMsg={errorMessage} />
            )}
            {isPaymentInProgress && (
                <StyledAlertText>
                    <Text paddingRight="sm">
                        Payment in progress.
                    </Text>
                    <Spinner />
                </StyledAlertText>
            )}
            <StyledButtonGroup aria-label="Button submit">
                <Button
                    buttonType="primary"
                    disabled={!canSubmit() || disableControls() || isSubmitting}
                    onClick={handleSubmit}
                    id="BillingPaymentMethods-submit"
                >
                    {submitLabel}
                </Button>
            </StyledButtonGroup>
            {disableImpersonatorPayments && (
                <Text fontType="finePrintHeading" as="p">
                    Editing disabled while impersonating
                </Text>
            )}
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    accountPaymentMethodsLoadingState: state.billing.accountPaymentMethods.loadingState,
    accountPaymentMethods: state.billing.accountPaymentMethods.accountPaymentMethods,
    paymentMethodLock: state.billing.accountPaymentMethods.paymentMethodLock,
    isAutopayEnabled: state.billing.accountPaymentMethods.isAutopayEnabled,
    autopayMethodId: state.billing.accountPaymentMethods.autopayMethodId,
    autopayOwnerId: state.billing.accountPaymentMethods.autopayOwnerId,
    userCanAccessAch: state.billing.accountPaymentMethods.canAccessAch,
    user: state.user,
    defaultPaymentMethod: state.billing.accountPaymentMethods.defaultPaymentMethod,
});

BillingPaymentMethods.propTypes = propTypes;
BillingPaymentMethods.defaultProps = defaultProps;

export { BillingPaymentMethods };
export default connect(mapStateToProps)(BillingPaymentMethods);
